<script setup lang="ts">
import {
    ExternalLinkIcon,
    IdentificationIcon,
    LocationMarkerIcon,
    OfficeBuildingIcon,
    ReceiptTaxIcon,
} from '@heroicons/vue/solid'
import { computed, onBeforeMount, reactive, ref, watch } from 'vue'
import { usePersonalAccountDataState } from '../../../../utilities/account/useAccountDataPersonal'
import { kycProviderExternal } from '../../../../utilities/helpers'
import { isRequiredNaturalProfileDocuments, isVisibleNaturalProfileDocuments } from '../../../../validation'
import { i18n } from '../../../../localization'
import AccountKycProcessTabs, { KycDocumentTabs } from '../../common/verification/AccountKycProcessTabs.vue'
import {
    AccountNavigationStore,
    useAccountNavigationState,
} from '../../../../utilities/account/useAccountNavigationPersonal'
import { getPlatformSettings } from '../../../../utilities/usePlatformSettings'
import AccountDepotForm from '../../common/verification/AccountDepotForm.vue'
import AccountKycProcessExternal from './AccountKycProcessExternal.vue'

const store = usePersonalAccountDataState()
const navigationStore = useAccountNavigationState() as AccountNavigationStore
const { naturalProfile, kycDocuments: accountKycDocuments } = store
const platformSettings = getPlatformSettings()
const showDepot = computed(() => platformSettings.investment_desk_settings.show_depot_fields_on_profile_section)

onBeforeMount(() => {
    setKycDocs()
})

watch(accountKycDocuments, () => {
    setKycDocs()
})

function setKycDocs() {
    Object.keys(kycDocuments).forEach((key) => {
        if (kycDocuments.hasOwnProperty(key)) {
            kycDocuments[key].documents = []
        }
    })
    accountKycDocuments.forEach((kycD) => {
        if (kycDocuments.hasOwnProperty(kycD.verification_type)) {
            kycDocuments[kycD.verification_type].documents.push(kycD)
        }
    })
}

const kycDocuments = reactive({
    kyc_external: {
        header: i18n.global.t('kyc_provider.' + store.naturalProfile.platform_kyc_provider),
        icon: ExternalLinkIcon,
        accepted: computed(() => naturalProfile.kyc_id),
        required: isRequiredNaturalProfileDocuments('identity'),
        documents: [],
        show: kycProviderExternal.value,
    },
    kyc_id: {
        header: i18n.global.t('verification.identity_verification'),
        icon: IdentificationIcon,
        accepted: computed(() => naturalProfile.kyc_id),
        required: getPlatformSettings().require_kyc_id_for_investments,
        documents: [],
        show: !kycProviderExternal.value && computed(() => isVisibleNaturalProfileDocuments('identity')),
        subheadline: i18n.global.t('verification.identity_verification_subheadline'),
    },
    kyc_tax: {
        header: i18n.global.t('verification.tax_verification'),
        icon: ReceiptTaxIcon,
        accepted: computed(() => naturalProfile.kyc_tax),
        required: getPlatformSettings().require_kyc_tax_for_investments,
        documents: [],
        show: computed(() => isVisibleNaturalProfileDocuments('tax')),
        subheadline: i18n.global.t('verification.tax_verification_headline'),
    },
    kyc_address: {
        header: i18n.global.t('verification.address_verification'),
        accepted: computed(() => naturalProfile.kyc_address),
        required: getPlatformSettings().require_kyc_address_for_investments,
        icon: LocationMarkerIcon,
        documents: [],
        headline: i18n.global.t('verification.address_verification_headline'),
        show: computed(() => isVisibleNaturalProfileDocuments('address')),
        subheadline: i18n.global.t('verification.address_verification_subheadline'),
    },
    depot: {
        header: i18n.global.t('verification.depot'),
        icon: OfficeBuildingIcon,
        show: showDepot,
        subheadline: i18n.global.t('verification.depot_verification_subheadline'),
    },
})

// if no tab is provided select kyc_id
const currentTabKey = ref<keyof KycDocumentTabs>(
    navigationStore.tab ??
        Object.keys(kycDocuments).filter((d) => {
            return kycDocuments[d].show ? d : false
        })[0]
)
watch(currentTabKey, (val) => (navigationStore.tab = val))
</script>

<template>
    <AccountKycProcessTabs v-model:tab-key="currentTabKey" :kyc-document-tabs="kycDocuments" :account-store="store">
        <template #body>
            <div v-if="currentTabKey === 'kyc_external'">
                <AccountKycProcessExternal />
            </div>
            <div v-if="currentTabKey === 'depot'">
                <AccountDepotForm profile-type="natural" />
            </div>
        </template>
    </AccountKycProcessTabs>
</template>
